import * as S from './styles';
import { colors } from '@/constants';
import { useGlobals, useCartTotals, useCart, useClient, useCorporateFlags } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa6';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, SearchField } from '@/components';
import { formatCurrency, getSegmentCode } from '@/shared';
import * as R from '@/constants/systemRoutes';
import { CorporateFlagsTheme } from '@/types';

interface Props {
  id: string;
  value: string;
  showUser?: boolean;
  showMicrophone?: boolean;
  showBackButton?: boolean;
  showPaymentMode?: boolean;
  showCancelButton?: boolean;
  onFocus?: (s: any) => void;
  setFilter: (s: any) => void;
  isExactSearch?: boolean;
  setIsExactSearch?: (s: boolean) => void;
  showCamera?: boolean;
  placeholder?: string;
  boxShadow?: string;
  /* @deprecated */
  forceReload?: boolean;
  padding?: string;
}

const SearchView = ({
  id,
  value,
  showUser,
  showBackButton,
  showPaymentMode,
  showCancelButton,
  onFocus,
  setFilter,
  isExactSearch,
  setIsExactSearch,
  showMicrophone,
  showCamera,
  placeholder,
}: Props) => {
  const navigation = useNavigate();
  const { brandTheme, brands, setSelectedBrand, getMatchedPairForBrand, selectedBrandPair } = useCorporateFlags();
  const { totalQuantity } = useCart();
  const { clientPayments, selectedClient } = useClient();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { fetchTotalPriceByPaymentCode } = useCartTotals();
  const { paymentCode, setPaymentCode } = useGlobals();
  const [selectedPaymentCode, setSelectedPaymentCode] = useState(paymentCode);

  /** Condições da loja ativa (brand pair), não o merge da raiz do cliente. */
  const scopedClientPayments = useMemo(() => {
    const pair = selectedBrandPair;
    const lojas = Array.isArray(selectedClient?.lojas) ? selectedClient.lojas : [];

    if (
      pair != null &&
      Number.isFinite(Number(pair.codLoja)) &&
      Number.isFinite(Number(pair.codSegmento)) &&
      lojas.length > 0
    ) {
      const codLoja = Number(pair.codLoja);
      const codSegmento = Number(pair.codSegmento);

      const matchedLoja = lojas.find((loja: any) => {
        if (Number(loja?.COD_LOJA) !== codLoja) return false;
        const segmentos = Array.isArray(loja?.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
        return segmentos.some((s: any) => getSegmentCode(s) === codSegmento);
      });

      const pay = matchedLoja?.COD_PAGAMENTO;
      if (Array.isArray(pay) && pay.length > 0) {
        return [...pay].sort((a, b) => a.cod_condicao - b.cod_condicao);
      }
    }

    return clientPayments;
  }, [selectedBrandPair, selectedClient?.lojas, clientPayments]);

  const currentPayment = useMemo(() => {
    return scopedClientPayments?.find(({ cod_condicao }) => cod_condicao === paymentCode)?.desc_condicao;
  }, [scopedClientPayments, paymentCode]);

  const currentPaymentTotal = useMemo(() => {
    return fetchTotalPriceByPaymentCode(paymentCode);
  }, [fetchTotalPriceByPaymentCode, paymentCode]);

  const setGlobalPaymentCode = useCallback(() => {
    setPaymentCode(selectedPaymentCode);
    setIsOpenModal(false);
  }, [selectedPaymentCode, setPaymentCode]);

  const handleFilter = useCallback(
    (filter: string) => {
      setFilter(filter);
    },
    [setFilter]
  );

  useEffect(() => {
    if (isOpenModal) setSelectedPaymentCode(paymentCode);
  }, [isOpenModal, paymentCode]);

  useEffect(() => {
    if (!scopedClientPayments?.length) return;
    const allowed = new Set(scopedClientPayments.map((p) => p.cod_condicao));
    if (allowed.has(paymentCode)) return;
    const next = scopedClientPayments[0].cod_condicao;
    setPaymentCode(next);
    setSelectedPaymentCode(next);
  }, [scopedClientPayments, paymentCode, setPaymentCode]);

  const goBack = () => navigation(-1);

  const chooseBrand = (brand: CorporateFlagsTheme) => {
    setSelectedBrand(brand);
  };

  return (
    <>
      <S.StickyContent>
        <S.BrandContent $bgColor={brandTheme?.bg_primary_color} $borderColor={brandTheme?.border_color}>
          <S.Scroll $borderColor={brandTheme?.border_color}>
            {brands.map((brand, ind) => {
      
              //const isActive = brand.name === brandTheme?.name;
              const isActive = brand.cod_loja.some((pair) => {
                if (!selectedBrandPair) return false;

                return Number(pair.cod_loja) === selectedBrandPair.codLoja && Number(pair.cod_segmento) === selectedBrandPair.codSegmento;
              });

              const pairForBrand = getMatchedPairForBrand(brand);
              const showFortStoreCode =
                (brand.name || '').toLowerCase().includes('fort') && pairForBrand != null;

              return (
                <S.BrandItem
                  onClick={() => chooseBrand(brand)}
                  key={`brand-item-${brand.name}-${ind}`}
                  $isActive={isActive}
                  $textColor={brandTheme?.text_color}
                  $bgColor={isActive ? brandTheme?.bg_active_color : brandTheme?.bg_inactive_color}
                  $borderColor={isActive ? brandTheme?.border_color : `${brandTheme?.border_color}`}
                >
                  <S.BrandItemImage src={`data:image/png;base64,${brand.image}`} alt={brand.name} $isActive={isActive} />
                  <S.BrandItemText $textColor={isActive ? brandTheme?.active_color : colors.brand.white}>
                    {brand.name}
                    {showFortStoreCode && (
                      <S.BrandItemStoreCode> ({pairForBrand.codLoja})</S.BrandItemStoreCode>
                    )}
                  </S.BrandItemText>
                </S.BrandItem>
              );
            })}
          </S.Scroll>
        </S.BrandContent>

        <S.SearchContent $borderColor={brandTheme?.border_color} $bgColor={brandTheme?.bg_active_color}>
          <S.Row>
            {showBackButton && (
              <S.Button onClick={goBack}>
                <S.ArrowLeftLine size={20} color={brandTheme?.text_color} />
              </S.Button>
            )}

            <SearchField
              id={id}
              onFocus={onFocus}
              value={value}
              isExactSearch={isExactSearch}
              setIsExactSearch={setIsExactSearch}
              showMicrophone={showMicrophone}
              showCamera={showCamera}
              setInput={handleFilter}
              placeholder={placeholder}
            />

            {showCancelButton && (
              <S.CancelButton $show={!!value} $color={brandTheme?.text_color} onClick={() => setFilter('')} data-test="search-page-clear-field">
                Cancelar
              </S.CancelButton>
            )}

            {showUser && (
              <S.Button onClick={() => navigation(R.MY_CLIENTS.link)}>
                <S.AccountBoxLine color={brandTheme?.text_color} size={24} />
              </S.Button>
            )}
          </S.Row>

          {showPaymentMode && (
            <S.RowButton onClick={() => setIsOpenModal(true)} data-test="component-search-button-open-payment">
              <S.GridRow>
                <S.PaymentIcon size={20} color={brandTheme?.text_color || colors.brand.white} />

                <S.PaymentMode $color={brandTheme?.text_color}>
                  {currentPayment}
                  {currentPaymentTotal > 0 && totalQuantity > 0 && (
                    <S.FinalPrice>• {formatCurrency({ value: currentPaymentTotal })}</S.FinalPrice>
                  )}
                </S.PaymentMode>
                <FaChevronDown color={brandTheme?.text_color || colors.brand.white} />
              </S.GridRow>
            </S.RowButton>
          )}
        </S.SearchContent>
      </S.StickyContent>

      <Modal id="search" closeModal={setIsOpenModal} isOpen={isOpenModal}>
        <S.ModalContet>
          <S.ModalTitle>Prazos e Formas de pagamento</S.ModalTitle>

          <S.ListBg>
            {scopedClientPayments?.map((item) => {
              const isActive = item.cod_condicao === selectedPaymentCode;
              const priceCond = fetchTotalPriceByPaymentCode(item.cod_condicao);
              const formattedPrice = formatCurrency({ value: priceCond });

              if (!priceCond && totalQuantity) return '';

              return (
                <S.PaymendCard
                  $isactive={isActive}
                  key={`paymend-card-${item.cod_condicao}`}
                  onClick={() => setSelectedPaymentCode(item.cod_condicao)}
                  data-test={`search-component-payment-button-${item.cod_condicao}`}
                >
                  <S.CheckContent>
                    {isActive ? <S.CheckFill size={24} color={colors.green} /> : <S.CheckBlank size={24} color={colors.grey4} />}
                  </S.CheckContent>

                  <S.PaymentIcon size={18} color={isActive ? colors.brand.grafite : colors.gray.gray40} />

                  <S.TitlePaymentForm>
                    {item.desc_condicao}
                    {priceCond > 0 && totalQuantity > 0 && <S.FinalPrice>• {formattedPrice}</S.FinalPrice>}
                  </S.TitlePaymentForm>
                </S.PaymendCard>
              );
            })}
          </S.ListBg>

          <S.ButtonContent onClick={setGlobalPaymentCode} data-test="component-search-set-payment">
            <S.ButtonModal>Continuar</S.ButtonModal>
          </S.ButtonContent>
        </S.ModalContet>
      </Modal>
    </>
  );
};

export default SearchView;
